import * as React from 'react'

import { getTimeSpent, } from '../../../helpers/studentReports'
import {
  EXTRA_SMALL_ICON_BASE_SRC,
  Tooltip,
} from '../../../../Shared/index';

export const extraSmallLockIcon = <img alt="Lock" src={`${EXTRA_SMALL_ICON_BASE_SRC}/lock.svg`} />

export const SKILLS_REPORT_IMG_SRC = `${window.postbuildEnv.CDN_URL}/images/pages/skills_report`

export const smartGroupIllustrationSrc = `${SKILLS_REPORT_IMG_SRC}/smart_group_illustration.svg`

export const NO_CLASSROOM = { id: 'no-classroom' }

export const NO_STUDENT_ID = 'no-student-id'

// premium states
export const PREMIUM_STATE_NONE = 'none'
export const PREMIUM_STATE_LOCKED = 'locked'
export const PREMIUM_STATE_TRIAL = 'trial'
export const PREMIUM_STATE_PAID = 'paid'

// practice scopes
export const ALL_PRACTICE = 'All Practice'
export const CLASS_PRACTICE = 'Class Practice'

// smart groups
export const MEETING_GOAL = 'Meeting goal'
export const APPROACHING_GOAL = 'Approaching goal'
export const NEEDS_SUPPORT = 'Needs support'
export const NEEDS_MORE_PRACTICE = 'Needs more practice'
export const NOT_STARTED = 'Not started'

export const ORDERED_SMART_GROUPS = [MEETING_GOAL, APPROACHING_GOAL, NEEDS_SUPPORT, NEEDS_MORE_PRACTICE, NOT_STARTED]

// exposure levels
export const HIGH = 'High'
export const MEDIUM = 'Medium'
export const LOW = 'Low'
export const NONE = 'None'

// smart group styling and sorting
export const smartGroupToClassName = {
  [MEETING_GOAL]: 'meeting-goal',
  [APPROACHING_GOAL]: 'approaching-goal',
  [NEEDS_SUPPORT]: 'needs-support',
  [NEEDS_MORE_PRACTICE]: 'needs-more-practice',
  [NOT_STARTED]: 'not-started',
}

export const smartGroupToSort = {
  [MEETING_GOAL]: `1${MEETING_GOAL}`,
  [APPROACHING_GOAL]: `2${APPROACHING_GOAL}`,
  [NEEDS_SUPPORT]: `3${NEEDS_SUPPORT}`,
  [NEEDS_MORE_PRACTICE]: `4${NEEDS_MORE_PRACTICE}`,
  [NOT_STARTED]: `5${NOT_STARTED}`,
}

// Icon sources for formatting functions
const dashIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/dash.svg`
const arrowUpIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/arrow-up.svg`
const arrowDownIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/arrow-down.svg`
const warningIconSrc = `${EXTRA_SMALL_ICON_BASE_SRC}/warning.svg`

// Shared formatting functions
export const formatTime = (seconds: number) => {
  if (seconds === 0) return '—'
  
  return getTimeSpent(seconds)
};

export const lowExposureTooltip = () => (
  <Tooltip
    skipPositioning={true}
    tooltipText="This Momentum Score is not yet reliable due to low practice exposure. More practice is needed to accurately measure growth."
    tooltipTriggerText={<img alt="Warning icon" src={warningIconSrc} />}
  />
)

export const formatMomentumScore = (momentumScore: number, exposure: string) => {
  if (!momentumScore && momentumScore !== 0) return '—'

  return (
    <span className="momentum-score-and-tooltip">
      {momentumScore}
      {exposure === LOW && lowExposureTooltip()}
    </span>
  )
};

export const formatTrend = (trend: number, totalAnswered: number) => {
  if (totalAnswered === 0) return '—'
  if (trend === 0) return <span><img alt="" src={dashIconSrc} />Steady</span>
  if (trend === 1) return <span><img alt="" src={arrowUpIconSrc} />Trending Up</span>
  return <span><img alt="" src={arrowDownIconSrc} />Trending Down</span>
};

export const formatPercentage = (value: number, totalAnswered: number) => {
  if (totalAnswered === 0) { return '—' }
  return `${Math.round(value)}%`;
};

export const formatLastPracticed = (lastPracticed: string | null) => {
  if (!lastPracticed) return '—';
  return new Date(lastPracticed).toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  });
};

// Sort helper functions - return raw values for sorting
export const getSortableMomentumScore = (momentumScore: number | null) => {
  return momentumScore ?? -Infinity;
};

export const getSortableSmartGroup = (smartGroup: string, momentumScore: number | null, name: string | null, ) => {
  // Invert momentum score for descending order within each smart group (higher scores first)
  const invertedMomentumScore = 1000 - (getSortableMomentumScore(momentumScore));
  return `${smartGroupToSort[smartGroup]}-momentumScore${invertedMomentumScore.toString().padStart(4, '0')}-name${name}`
};

export const getSortableTrend = (trend: number | null) => {
  return trend ?? -Infinity;
};

export const getSortablePercentage = (value: number | null, totalAnswered: number) => {
  if (totalAnswered === 0) return 0;
  return Math.round(value ?? 0);
};

export const getSortableTime = (seconds: number | null) => {
  return seconds ?? 0;
};

export const getSortableLastPracticed = (lastPracticed: string | null) => {
  if (!lastPracticed) return '';
  return lastPracticed; // ISO string sorts correctly
};

export const getSortableExposure = (exposure: string) => {
  // Custom sorting: High > Medium > Low > None
  const exposureOrder = {
    [HIGH]: '1High',
    [MEDIUM]: '2Medium', 
    [LOW]: '3Low',
    [NONE]: '4None'
  };
  return exposureOrder[exposure] || '5' + exposure;
};

export const getSmartGroup = (exposure: string, ema: number, totalAnswered: number, momentumScoreGoal: number, ignoreExposure:boolean = false) => {
  if (totalAnswered === 0) return NOT_STARTED;
  if (!ignoreExposure && exposure === LOW) return NEEDS_MORE_PRACTICE;
  if (ema >= momentumScoreGoal) return MEETING_GOAL;
  if (ema >= momentumScoreGoal - 10) return APPROACHING_GOAL;
  return NEEDS_SUPPORT
};

export const renderNoClassroomEmptyState = () => (
  <section className="empty-state">
    <h2>Create a class to use this report</h2>
    <a className="quill-button green contained medium focus-on-light" href="/teachers/classrooms">Go to Manage Classes</a>
  </section>
)
